import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Month names in the viewer's locale, short form for the grid.
const MONTHS = Array.from({ length: 12 }, (_, m) =>
  new Date(2000, m, 1).toLocaleString(undefined, { month: 'short' })
);

/**
 * The month label doubles as the jump control: stepping a year at a time with
 * chevrons meant twelve clicks to reach next March.
 */
export default function MonthYearPicker({ value, onChange, today = new Date(), label }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(value.y);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  // Reopening should always start from where the calendar actually is, not
  // from wherever the year stepper was left last time.
  useEffect(() => {
    if (open) setYear(value.y);
  }, [open, value.y]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (m) => {
    onChange({ y: year, m });
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Jump to a month"
        className="flex items-center gap-1 rounded-lg px-2 py-1 text-lg font-bold text-blue-900 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
      >
        <span className="min-w-[9rem] text-center">{label}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose month and year"
          className="absolute left-0 top-full z-30 mt-1 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setYear((y) => y - 1)}
              aria-label="Previous year"
              className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="font-rasa font-semibold text-gray-800 dark:text-gray-100 tabular-nums">{year}</span>
            <button
              type="button"
              onClick={() => setYear((y) => y + 1)}
              aria-label="Next year"
              className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((name, m) => {
              const isCurrent = value.y === year && value.m === m;
              const isThisMonth = today.getFullYear() === year && today.getMonth() === m;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => pick(m)}
                  aria-current={isCurrent ? 'true' : undefined}
                  className={`rounded-md py-1.5 font-rasa text-sm transition-colors ${
                    isCurrent
                      ? 'bg-blue-500 text-white font-medium'
                      : isThisMonth
                        ? 'text-blue-600 dark:text-blue-400 ring-1 ring-inset ring-blue-300 dark:ring-blue-500/40 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              onChange({ y: today.getFullYear(), m: today.getMonth() });
              setOpen(false);
              triggerRef.current?.focus();
            }}
            className="mt-3 w-full rounded-md border border-gray-200 dark:border-gray-600 py-1.5 font-rasa text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Jump to this month
          </button>
        </div>
      )}
    </div>
  );
}
